/**
 * WebSocket Store
 * Manages Socket.IO connection state using Jotai
 */

import { atom } from 'jotai';
import { io, type Socket } from 'socket.io-client';
import { chatMessagesAtom } from '@/stores/chat.store';
import {
  EMPTY_SIMULATION_PROFILE,
  simulationProfileAtom,
  openSectionsAtom,
} from '@/modules/simulator/sidebar/simulation-profile.store';
import {
  appendInvestigationStepAtom,
  clearRunReviewsAtom,
  concludeInvestigationAtom,
} from '@/modules/simulator/review/review.store';
import {
  conversationIdAtom,
  describedScenarioAtom,
  EMPTY_DESCRIBED_SCENARIO,
  loadedThreadAtom,
  reopenedLinksAtom,
  runOnScreenAtom,
} from '@/modules/simulator/session/simulator-session.store';
import { cancelDescribeFlowAtom } from '@/modules/simulator/describe/describe-flow';
import { SIMULATOR_RUNS_QUERY_ROOT } from '@/modules/simulator/runs/use-simulator-runs';
import { invalidateQueries, queryClient, refreshQueries } from '@/lib/query-client';
import { BULK_RUNS_QUERY_ROOT, DATASET_RUNS_QUERY_ROOT } from '@/modules/back-testing/runs/use-run-pages';
import { backTestingTypesKey, setProtocolCompiling, setReconcileProgress } from '@/hooks/use-back-testing-types';
import { captureException, addBreadcrumb } from '@/lib/sentry/sentry-utils';
import { ERROR_LEVELS, TAGS } from '@/constants/sentry-constants';
import { isInternalTraceStep } from '@/adapters/sentinelWorkspace.adapter';
import type {
  WebSocketStatus,
  ClientToServerEvents,
  ServerToClientEvents,
  AuthenticatePayload,
  MessagePayload,
  TestRuleResultPayload,
  ToolExecutionEventPayload,
  ThinkingEventPayload,
  SegNodeDetail,
  SegExecutionTrace,
  ErrorPayload,
  SourceStatusUpdatedPayload,
  SourceStatusUpdateErrorPayload,
  WorkflowCompiledPayload,
  WorkflowCompileFailedPayload,
  HumanApprovalRequestPayload,
  PersonaGeneratedPayload,
  PersonaGenerationFailedPayload,
  DecisionQuestionPayload,
  DecisionProgressPayload,
  DecisionFailedPayload,
  DecisionCompletePayload,
  DecisionScoredPayload,
  BackTestingBucketingPayload,
  BackTestingBulkRunPayload,
  GapTestRunPayload,
  BackTestingInvestigationPayload,
  BackTestingInvestigationStepPayload,
  SentinelTicketAssignedPayload,
  TicketPresencePayload,
  TicketViewersPayload
, PlaygroundChatTurnPayload } from '@/types/websocket.types';
import type {
  SimulationDatasetCasePayload,
  SimulationDatasetCaseStepPayload,
  SimulationDatasetRunPayload
} from '@/types/simulation-dataset.types';

// ============================================================================
// Configuration
// ============================================================================

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3000';
const DEFAULT_TOOL_EXECUTION_TEXT = 'Processing...';
const TOOL_EXECUTION_TIMEOUT = 30000; // 30 seconds

// ============================================================================
// Socket Instance (singleton)
// ============================================================================

let socketInstance: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
let toolTimeoutId: ReturnType<typeof setTimeout> | null = null;
let lastProcessedRequestId: string | null = null;
let lastThinkingRequestId: string | null = null;
// Request ids of the current run: account-broadcast events outside this set are
// ignored, so another session's run can never reset the live timeline.
let runRequestIds = new Set<string>();

// ============================================================================
// State Atoms
// ============================================================================

/**
 * WebSocket connection status
 */
export const websocketStatusAtom = atom<WebSocketStatus>('disconnected');

/**
 * Client ID for the current connection
 */
export const clientIdAtom = atom<string | null>(null);

/**
 * Socket ID assigned by server
 */
export const socketIdAtom = atom<string | null>(null);

/**
 * Connection error message
 */
export const connectionErrorAtom = atom<string | null>(null);

/**
 * Whether a request is currently waiting for response
 */
export const isWaitingAtom = atom<boolean>(false);

/**
 * Tool execution state
 */
export const toolExecutionTextAtom = atom<string>('');
export const isToolExecutingAtom = atom<boolean>(false);

/**
 * Last received rules from test rule result
 */
export const lastRulesUsedAtom = atom<TestRuleResultPayload['rules_used'] | null>(null);

/**
 * Per-node execution states for the currently active SEG run
 * Map of node_id → execution state
 */
export const segNodeStatesAtom = atom<Map<string, 'idle' | 'running' | 'success' | 'error'>>(new Map());

/**
 * The seg_id of the currently executing (or last executed) run
 */
export const activeSegIdAtom = atom<string | null>(null);

/**
 * Last completed SEG execution trace (post-execution)
 */
export const lastSegExecutionTraceAtom = atom<SegExecutionTrace | null>(null);

/**
 * Per-node rich detail captured from real-time thinking_event (node_end)
 * Map of node_id → SegNodeDetail (inputs, output, error_detail, duration_ms, etc.)
 * Populated during execution; survives until the next test run clears it.
 */
export const segNodeDetailsAtom = atom<Map<string, SegNodeDetail>>(new Map());

/**
 * Ordered list of node_ids as they completed (for the execution log panel)
 */
export const segNodeOrderAtom = atom<string[]>([]);

/**
 * Human-readable thinking step text for the currently executing node.
 * Generated by Brahma and streamed via node_start events.
 * Cleared when the final response arrives (isWaiting → false).
 */
export const activeThinkingTextAtom = atom<string | null>(null);

/**
 * Ordered thinking steps for the simulator timeline. Appended on node_start,
 * resolved in place on the paired node_end (matched by node_id). Resets per request.
 */
export interface ThinkingStep {
  nodeId: string;
  nodeType: string;
  label: string;
  thinkingText?: string;
  timestamp: number;
  requestId: string;
  // Tool-call fields: name/args from node_start, duration/outcome from node_end.
  toolName?: string;
  toolProvider?: string;
  toolArgs?: Record<string, unknown>;
  durationMs?: number;
  outcome?: 'success' | 'error' | 'awaiting_approval';
  resultSummary?: string;
  resultItems?: string[];
  done?: boolean;
  /** A write, which a human approves in production; a simulation never executes it. */
  isWrite?: boolean;
  /** A mocked tool call; its full output rides the step for visibility. */
  simulated?: boolean;
  /** A write, simulated rather than executed. */
  simulatedWrite?: boolean;
  /** The output was bent to the loaded scenario's type. */
  grounded?: boolean;
  toolOutput?: unknown;
}

export const thinkingStepsAtom = atom<ThinkingStep[]>([]);

export interface SentinelWorkspaceSocketIdentity {
  ticketId: number;
  conversationId: number;
  rootTraceId: string | null;
  requestId: string | null;
  /** Runs left behind when the next one started; their late steps are not ours. */
  ignoredRequestIds?: string[];
}

export function isActiveSentinelThinkingEvent(
  data: ThinkingEventPayload,
  active: SentinelWorkspaceSocketIdentity | null,
): boolean {
  if (
    !active ||
    Number(data.sentinel_ticket_id) !== active.ticketId ||
    data.root_trace_id !== active.rootTraceId ||
    !data.sentinel_request_id
  ) {
    return false;
  }
  if (active.requestId) return data.sentinel_request_id === active.requestId;
  // The new run's id is not known yet, so anything already on the ticket is not it.
  return !active.ignoredRequestIds?.includes(data.sentinel_request_id);
}

/** Live-only Sentinel steps. They are deliberately never hydrated from the audit log. */
export const sentinelThinkingStepsAtom = atom<ThinkingStep[]>([]);

export function mergeSentinelThinkingEvent(
  previous: ThinkingStep[],
  data: ThinkingEventPayload,
  sentinelRequestId: string,
): ThinkingStep[] {
  const index = previous.findIndex(
    (step) => step.nodeId === data.node_id && step.requestId === sentinelRequestId,
  );
  if (data.event === 'node_start') {
    const started = {
      nodeId: data.node_id,
      nodeType: data.node_type,
      label: data.node_label || data.thinking_text || data.node_id,
      thinkingText: data.thinking_text,
      timestamp: data.timestamp || Date.now(),
      requestId: sentinelRequestId,
      toolName: data.tool_name,
      ...(data.tool_provider ? { toolProvider: data.tool_provider } : {}),
      toolArgs: data.tool_args,
    };
    if (index >= 0) {
      return previous.map((step, stepIndex) => (stepIndex === index ? { ...step, ...started } : step));
    }
    return [
      ...previous,
      started,
    ];
  }
  const completed = {
    done: true,
    durationMs: data.duration_ms,
    outcome: data.outcome,
    resultSummary: data.result_summary,
    resultItems: data.result_items,
    toolOutput: data.tool_output,
    ...(data.tool_provider ? { toolProvider: data.tool_provider } : {}),
  };
  if (index < 0) {
    return [
      ...previous,
      {
        nodeId: data.node_id,
        nodeType: data.node_type,
        label: data.node_label || data.thinking_text || data.node_id,
        thinkingText: data.thinking_text,
        timestamp: data.timestamp || Date.now(),
        requestId: sentinelRequestId,
        toolName: data.tool_name,
        ...completed,
      },
    ];
  }
  return previous.map((step, stepIndex) =>
    stepIndex === index
      ? {
          ...step,
          ...completed,
        }
      : step,
  );
}
export const sentinelWorkspaceSocketIdentityAtom = atom<SentinelWorkspaceSocketIdentity | null>(null);

/** Emits a room join/leave and leaves a breadcrumb — joins have no server ack, so this is the only trace. */
function emitSentinelRoom(action: 'join_room' | 'leave_room', conversationId: number): void {
  const connected = Boolean(socketInstance?.connected);
  if (connected) socketInstance!.emit(action, `conversation:${conversationId}`);
  addBreadcrumb({
    category: 'websocket',
    message: connected ? `sentinel ${action}` : `sentinel ${action} skipped: socket disconnected`,
    level: connected ? ERROR_LEVELS.INFO : ERROR_LEVELS.WARNING,
    data: { room: `conversation:${conversationId}` },
  });
}

/** Compared by content, or the same runs in a new array would look like a change. */
function sameRequestIds(left: string[] | undefined, right: string[] | undefined): boolean {
  return (left ?? []).join('|') === (right ?? []).join('|');
}

export const setSentinelWorkspaceSocketIdentityAtom = atom(
  null,
  (get, set, identity: SentinelWorkspaceSocketIdentity | null) => {
    const previous = get(sentinelWorkspaceSocketIdentityAtom);
    const unchanged =
      previous?.ticketId === identity?.ticketId &&
      previous?.conversationId === identity?.conversationId &&
      previous?.rootTraceId === identity?.rootTraceId &&
      previous?.requestId === identity?.requestId &&
      sameRequestIds(previous?.ignoredRequestIds, identity?.ignoredRequestIds);
    if (unchanged) return;
    if (previous) emitSentinelRoom('leave_room', previous.conversationId);
    set(sentinelWorkspaceSocketIdentityAtom, identity);
    set(sentinelThinkingStepsAtom, []);
    if (identity) emitSentinelRoom('join_room', identity.conversationId);
  }
);

/** Who is on each Sentinel ticket, as last announced by the server. A ticket nobody is on has no entry. */
export const ticketViewersAtom = atom<Record<number, TicketViewersPayload['viewers']>>({});

/** Sends a presence beat for a ticket, or leaves it when given null. */
export const sendTicketPresenceAtom = atom(null, (_get, _set, beat: TicketPresencePayload | null) => {
  if (!socketInstance?.connected) return;
  if (beat) socketInstance.emit('ticket_presence', beat);
  else socketInstance.emit('ticket_presence_leave');
});

/**
 * Live online/offline changes announced since the page loaded, keyed `{buId}:{userId}` (one
 * switch per brand). The team table merges these over its fetched snapshot; true = came
 * online, false = went offline.
 */
export const sentinelPresenceChangesAtom = atom<Record<string, boolean>>({});

/**
 * When each change in `sentinelPresenceChangesAtom` arrived, as a running number per key. A
 * snapshot read from the server already includes every change numbered before it was asked
 * for, so only later ones may be applied over it (use-sentinel-routing.ts).
 */
export const sentinelPresenceOrderAtom = atom<Record<string, number>>({});
let presenceChangeCount = 0;

/** The number of presence changes received so far: a snapshot asked for now covers all of them. */
export function presenceChangesSoFar(): number {
  return presenceChangeCount;
}

/**
 * The latest ticket given to someone in this account, as announced. The workspace notifies
 * its own user about the ones addressed to them (use-sentinel-assignment-notifications.ts).
 */
export const latestSentinelAssignmentAtom = atom<SentinelTicketAssignedPayload | null>(null);

/** Ticket lists change when a ticket is routed or leaves a queue; refetch them. */
function refreshSentinelTicketLists(): void {
  void invalidateQueries(['sentinel-workspace', 'tickets']);
  void invalidateQueries(['sentinel', 'unassigned-counts']);
  void invalidateQueries(['sentinel', 'view-counts']);
}

/** Which Sentinel ticket an event is about: by its id, or by its conversation for Open / Closed. */
type SentinelTicketRef = { ticketId: number } | { conversationId: number };

/**
 * Refetches the ticket's detail when it is open on screen. A ticket waiting on a person does not
 * poll, so without this a change the backend made (automation's reply, a helpdesk close) only
 * shows after reselecting the ticket. Only queries in use refetch, so this is at most one GET.
 */
function refreshSentinelTicketDetail(ref: SentinelTicketRef): void {
  void queryClient.invalidateQueries({
    predicate: ({ queryKey, state }) => {
      if (queryKey[0] !== 'sentinel-workspace' || queryKey[1] !== 'conversation') return false;
      if ('ticketId' in ref) return Number(queryKey[3]) === ref.ticketId;
      const detail = state.data as { ticket?: { conversation_id?: number } } | undefined;
      return Number(detail?.ticket?.conversation_id) === ref.conversationId;
    },
  });
}

function refreshSentinelTicket(ref: SentinelTicketRef): void {
  refreshSentinelTicketLists();
  refreshSentinelTicketDetail(ref);
}

/** Starts or stops the feed of every ticket's viewers. Starting again asks for a fresh snapshot. */
export const watchTicketPresenceAtom = atom(null, (_get, _set, watching: boolean) => {
  if (!socketInstance?.connected) return;
  socketInstance.emit(watching ? 'ticket_presence_watch' : 'ticket_presence_unwatch');
});

/**
 * Latest completed AI run's metadata, parsed from the response
 * additionalAttributes (all string-encoded on the wire). Drives the
 * run-details card. Reset on send / clear.
 */
export interface RunIntent {
  type: string;
  confidence?: number;
  category?: string;
  subcategory?: string;
}

/** A run that replayed a recorded ticket's AI turn, with the tools its recording could not answer. */
interface RunReplay {
  unanswered: string[];
}

export interface RunDetails {
  requestId?: string;
  status?: string;
  /** Set when the run answered from a recorded ticket's real turn. */
  replay?: RunReplay;
  confidence?: number;
  routing?: string;
  automationBlockReason?: string;
  /** Draft scored below the surfacing confidence threshold: shown in the simulator, held back in production. */
  belowConfidenceThreshold?: boolean;
  finalIntent: string[];
  intents: RunIntent[];
  kbIds: string[];
  segIds: string[];
  ruleNames: string[];
  guardrails: string[];
  similarTicketIds: string[];
  cost?: number;
  tokens?: number;
  model?: string;
  traceUrl?: string;
  latencyMs?: number;
}

export const lastRunDetailsAtom = atom<RunDetails | null>(null);

/**
 * The latest run's failure message (from an `error` event), or null on success.
 * Mutually exclusive with a completed run: cleared on send, on a successful
 * response, and on reset, so the run-details rail can surface a failed run
 * instead of falling back to its "nothing ran yet" empty state.
 */
export const lastRunErrorAtom = atom<string | null>(null);

/** A proposed write-action tool from a HUMAN_APPROVAL_REQUEST. */
export interface RunApprovalTool {
  toolName: string;
  args: string;
  reason?: string;
}

/**
 * Non-null when the latest run paused for human approval of its writes
 * (HUMAN_APPROVAL_REQUEST) instead of replying. A simulator run simulates its
 * writes and should never pause, but if one does this is what renders it: the
 * handler clears isWaiting before setting this, so without it the run would
 * simply vanish. Mutually exclusive with a reply or error; cleared on send,
 * success, error and reset.
 */
export const lastRunApprovalAtom = atom<RunApprovalTool[] | null>(null);

/** Parse the socket HUMAN_APPROVAL_REQUEST into the proposed write tools. */
const parseRunApproval = (data: HumanApprovalRequestPayload): RunApprovalTool[] =>
  (data.approvals ?? []).map((a) => ({
    toolName: String(a.toolName ?? 'tool'),
    args: typeof a.rawArgsJson === 'string' ? a.rawArgsJson : '',
    reason: typeof a.reason === 'string' ? a.reason : undefined,
  }));

const parseJsonStringArray = (value: unknown): string[] => {
  if (typeof value !== 'string' || !value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map((x) => String(x)) : [];
  } catch {
    return [];
  }
};

/** What a replay did with its recording, off the reply's JSON-encoded report; undefined for a run that sent none. */
const replayFrom = (value: unknown): RunReplay | undefined => {
  if (typeof value !== 'string' || !value) return undefined;
  try {
    const report = JSON.parse(value) as { unanswered?: unknown };
    return { unanswered: Array.isArray(report.unanswered) ? report.unanswered.map(String) : [] };
  } catch {
    return undefined;
  }
};

/** Parse a reply's string-encoded additionalAttributes into RunDetails. */
export const runDetailsFrom = (a: Record<string, unknown>, requestId?: string, status?: string): RunDetails => {
  const num = (v: unknown): number | undefined => {
    const n = parseFloat(String(v));
    return Number.isFinite(n) ? n : undefined;
  };
  const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

  let intents: RunIntent[] = [];
  if (typeof a.enrichment_intents === 'string') {
    try {
      const parsed = JSON.parse(a.enrichment_intents);
      if (Array.isArray(parsed)) intents = parsed as RunIntent[];
    } catch {
      // malformed → leave empty
    }
  }

  const started = typeof a.requestProcessingStartedAt === 'string' ? Date.parse(a.requestProcessingStartedAt) : NaN;
  const ended = typeof a.requestProcessingEndedAt === 'string' ? Date.parse(a.requestProcessingEndedAt) : NaN;
  const kbIds = parseJsonStringArray(a.v3_kb_ids);

  return {
    requestId,
    status,
    replay: replayFrom(a.simulation_recording_report),
    confidence: num(a.v3_confidence),
    routing: str(a.v3_routing),
    automationBlockReason: str(a.v3_automation_block_reason),
    belowConfidenceThreshold: a.below_confidence_threshold === 'true',
    finalIntent: parseJsonStringArray(a.final_intent),
    intents,
    kbIds: kbIds.length ? kbIds : parseJsonStringArray(a.knowledgeBaseList),
    segIds: parseJsonStringArray(a.v3_seg_ids),
    ruleNames: parseJsonStringArray(a.ruleNames),
    guardrails: parseJsonStringArray(a.guardRailList),
    similarTicketIds: parseJsonStringArray(a.similar_ticket_ids),
    cost: num(a.cost),
    tokens: num(a.tokens),
    model: str(a.model),
    traceUrl: str(a.langfuse_trace_url),
    latencyMs: Number.isFinite(started) && Number.isFinite(ended) ? ended - started : undefined,
  };
};

// ============================================================================
// Message Handler Callbacks (stored outside atoms for performance)
// ============================================================================

type MessageHandler = (data: TestRuleResultPayload) => void;
type ErrorHandler = (data: ErrorPayload) => void;
type ToolExecutionHandler = (data: ToolExecutionEventPayload) => void;
type SourceStatusUpdateHandler = (data: SourceStatusUpdatedPayload) => void;
type SourceStatusErrorHandler = (data: SourceStatusUpdateErrorPayload) => void;
type WorkflowCompiledHandler = (data: WorkflowCompiledPayload) => void;
type WorkflowCompileFailedHandler = (data: WorkflowCompileFailedPayload) => void;
type ThinkingEventHandler = (data: ThinkingEventPayload) => void;
type PersonaGeneratedHandler = (data: PersonaGeneratedPayload) => void;
type PersonaGenerationFailedHandler = (data: PersonaGenerationFailedPayload) => void;
type DecisionQuestionHandler = (data: DecisionQuestionPayload) => void;
type DecisionProgressHandler = (data: DecisionProgressPayload) => void;
type DecisionFailedHandler = (data: DecisionFailedPayload) => void;
type DecisionCompleteHandler = (data: DecisionCompletePayload) => void;
type DecisionScoredHandler = (data: DecisionScoredPayload) => void;
type BackTestingBucketingHandler = (data: BackTestingBucketingPayload) => void;
type BackTestingBulkRunHandler = (data: BackTestingBulkRunPayload) => void;
type GapTestRunHandler = (data: GapTestRunPayload) => void;
type BackTestingInvestigationHandler = (data: BackTestingInvestigationPayload) => void;
type BackTestingInvestigationStepHandler = (data: BackTestingInvestigationStepPayload) => void;
type PlaygroundChatTurnHandler = (data: PlaygroundChatTurnPayload) => void;
type SimulationDatasetCaseHandler = (data: SimulationDatasetCasePayload) => void;
type SimulationDatasetCaseStepHandler = (data: SimulationDatasetCaseStepPayload) => void;
type SimulationDatasetRunHandler = (data: SimulationDatasetRunPayload) => void;

let messageHandler: MessageHandler | null = null;
let errorHandler: ErrorHandler | null = null;
let toolExecutionHandler: ToolExecutionHandler | null = null;
let sourceStatusUpdateHandler: SourceStatusUpdateHandler | null = null;
let sourceStatusErrorHandler: SourceStatusErrorHandler | null = null;
let workflowCompiledHandler: WorkflowCompiledHandler | null = null;
let workflowCompileFailedHandler: WorkflowCompileFailedHandler | null = null;
let thinkingEventHandler: ThinkingEventHandler | null = null;
let personaGeneratedHandler: PersonaGeneratedHandler | null = null;
let personaGenerationFailedHandler: PersonaGenerationFailedHandler | null = null;
let decisionQuestionHandler: DecisionQuestionHandler | null = null;
let decisionProgressHandler: DecisionProgressHandler | null = null;
let decisionFailedHandler: DecisionFailedHandler | null = null;
let decisionCompleteHandler: DecisionCompleteHandler | null = null;
let decisionScoredHandler: DecisionScoredHandler | null = null;
let backTestingBucketingHandler: BackTestingBucketingHandler | null = null;
let backTestingBulkRunHandler: BackTestingBulkRunHandler | null = null;
let gapTestRunHandler: GapTestRunHandler | null = null;
let backTestingInvestigationHandler: BackTestingInvestigationHandler | null = null;
let backTestingInvestigationStepHandler: BackTestingInvestigationStepHandler | null = null;
let playgroundChatTurnHandler: PlaygroundChatTurnHandler | null = null;
let simulationDatasetCaseHandler: SimulationDatasetCaseHandler | null = null;
let simulationDatasetCaseStepHandler: SimulationDatasetCaseStepHandler | null = null;
let simulationDatasetRunHandler: SimulationDatasetRunHandler | null = null;

// ============================================================================
// Helper Functions
// ============================================================================

const generateClientId = (): string => {
  return `client_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

const generateRequestId = (): string => {
  return `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

const clearToolTimeout = (): void => {
  if (toolTimeoutId) {
    clearTimeout(toolTimeoutId);
    toolTimeoutId = null;
  }
};

// ============================================================================
// Action Atoms
// ============================================================================

/**
 * Initialize WebSocket connection
 */
export const initializeWebSocketAtom = atom(null, (get, set) => {
  // Don't reinitialize if already connected
  if (socketInstance?.connected) {
    return socketInstance;
  }

  set(websocketStatusAtom, 'connecting');
  set(connectionErrorAtom, null);

  // Generate or reuse client ID
  let currentClientId = get(clientIdAtom);
  if (!currentClientId) {
    currentClientId = generateClientId();
    set(clientIdAtom, currentClientId);
  }

  // Create socket instance
  socketInstance = io(SOCKET_URL, {
    transports: ['websocket', 'polling'],
    auth: {
      clientId: currentClientId
    },
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000
  });

  // Connection events
  socketInstance.on('connect', () => {
    set(websocketStatusAtom, 'connected');
    set(connectionErrorAtom, null);
  });

  socketInstance.on('connected', (data) => {
    set(socketIdAtom, data.socketId);
    set(clientIdAtom, data.clientId);
  });

  socketInstance.on('success', (data) => {
    if (data.message !== 'Authenticated successfully') return;
    set(websocketStatusAtom, 'authenticated');
    const sentinelIdentity = get(sentinelWorkspaceSocketIdentityAtom);
    if (sentinelIdentity) emitSentinelRoom('join_room', sentinelIdentity.conversationId);
    // Assignments made while the socket was down were never announced to this tab, whether or
    // not a ticket is open (an empty list is exactly when one is awaited).
    refreshSentinelTicketLists();
  });

  socketInstance.on('disconnect', () => {
    set(websocketStatusAtom, 'disconnected');
    set(isWaitingAtom, false);
    clearToolTimeout();
    set(toolExecutionTextAtom, '');
    set(isToolExecutingAtom, false);
    set(segNodeStatesAtom, new Map());
    set(segNodeDetailsAtom, new Map());
    set(segNodeOrderAtom, []);
    set(activeThinkingTextAtom, null);
    set(sentinelThinkingStepsAtom, []);
    // Nobody can be vouched for while offline; they return with the next snapshot.
    set(ticketViewersAtom, {});
    lastProcessedRequestId = null;
    lastThinkingRequestId = null;
    // Keep runRequestIds: ids are unique per send, and wiping them here would
    // orphan an in-flight run when the socket reconnects mid-run.
  });

  socketInstance.on('connect_error', (error) => {
    set(websocketStatusAtom, 'error');
    set(connectionErrorAtom, error.message);
    set(isWaitingAtom, false);
    
    // Add Sentry breadcrumb and capture connection error
    addBreadcrumb({
      category: 'websocket',
      message: 'WebSocket connection error',
      level: ERROR_LEVELS.ERROR,
      data: {
        error: error.message,
        clientId: currentClientId,
        type: error.name,
      },
    });
    
    // Capture connection errors to Sentry
    captureException(error, {
      tags: {
        [TAGS.ERROR_TYPE]: 'websocket_connection',
      },
      contexts: {
        websocket: {
          clientId: currentClientId,
          errorType: error.name,
          errorMessage: error.message,
        },
      },
    });
  });

  socketInstance.on('error', (data) => {
    // A failure for a run this session didn't start must not kill our waiting
    // state or surface in the rail. Auth errors carry no request id and pass.
    if (data.request_id && !runRequestIds.has(data.request_id)) {
      return;
    }
    set(isWaitingAtom, false);
    clearToolTimeout();
    set(toolExecutionTextAtom, '');
    set(isToolExecutingAtom, false);

    if (data.code === 'AUTH_REJECTED') {
      // The server will not take these credentials (a bad token, an account this user cannot
      // reach). Asking again with the same ones would only loop; the next sign-in starts afresh.
      set(websocketStatusAtom, 'connected');
      console.warn('[WebSocket] Authentication rejected:', data.message);
      return;
    }
    if (data.code === 'AUTH_REQUIRED' || data.code === 'AUTH_FAILED') {
      set(websocketStatusAtom, 'connected');
      set(needsReauthAtom, true);
      return;
    }

    // Surface the failure in the run-details rail, above whatever the run reported before it stopped.
    set(lastRunErrorAtom, data.message || data.error || 'The run failed before producing a reply.');
    set(lastRunDetailsAtom, data.additionalAttributes ? runDetailsFrom(data.additionalAttributes, data.request_id) : null);
    set(lastRunApprovalAtom, null);

    // Add Sentry breadcrumb for WebSocket error
    addBreadcrumb({
      category: 'websocket',
      message: 'WebSocket error event',
      level: ERROR_LEVELS.ERROR,
      data: {
        error: data.error,
        message: data.message,
        code: data.code,
        requestId: data.request_id,
      },
    });

    // Capture WebSocket errors to Sentry
    const error = new Error(data.message || data.error || 'WebSocket error');
    captureException(error, {
      tags: {
        [TAGS.ERROR_TYPE]: 'websocket_error',
        [TAGS.ERROR_CODE]: data.code || 'unknown',
      },
      contexts: {
        websocket: {
          error: data.error,
          message: data.message,
          code: data.code,
          requestId: data.request_id,
        },
      },
    });

    errorHandler?.(data);
  });

  // A run paused for human approval ends with HUMAN_APPROVAL_REQUEST and no
  // `message` or `error`, which would hang the simulator on "Thinking…". Simulator
  // runs simulate their writes and should never pause; if one does, stop the wait
  // and show the proposed writes.
  socketInstance.on('human_approval_request', (data) => {
    // Broadcast account-wide for approval screens; act only on this session's run.
    const approvalRequestId = (data as { requestId?: string }).requestId;
    if (approvalRequestId && !runRequestIds.has(approvalRequestId)) {
      return;
    }
    set(isWaitingAtom, false);
    set(activeThinkingTextAtom, null);
    clearToolTimeout();
    set(toolExecutionTextAtom, '');
    set(isToolExecutingAtom, false);
    set(lastRunApprovalAtom, parseRunApproval(data));
    // Only the asking client's copy carries the run's attributes; the room copies, landing in either order, keep them.
    const approvalDetails = data.additionalAttributes ? runDetailsFrom(data.additionalAttributes, approvalRequestId) : null;
    if (approvalDetails || get(lastRunDetailsAtom)?.requestId !== approvalRequestId) set(lastRunDetailsAtom, approvalDetails);
    set(lastRunErrorAtom, null);
  });

  const handleMessageData = (data: TestRuleResultPayload) => {
    // Only accept replies for runs this session started: a reply carrying a
    // foreign request id belongs to another session on the same account (or a
    // stale run after Reset) and would inject its reply into this chat.
    if (data.request_id && !runRequestIds.has(data.request_id)) {
      return;
    }
    // Deduplicate: skip if we already processed this request_id
    if (data.request_id && data.request_id === lastProcessedRequestId) {
      return;
    }
    if (data.request_id) {
      lastProcessedRequestId = data.request_id;
    }

    set(isWaitingAtom, false);
    set(activeThinkingTextAtom, null);
    clearToolTimeout();
    set(toolExecutionTextAtom, '');
    set(isToolExecutingAtom, false);
    set(
      lastRunDetailsAtom,
      runDetailsFrom((data.additionalAttributes ?? {}) as Record<string, unknown>, data.request_id, data.status)
    );
    set(lastRunErrorAtom, null);
    set(lastRunApprovalAtom, null);

    if (data.rules_used) {
      set(lastRulesUsedAtom, data.rules_used);
    }

    if (data.seg_execution_trace) {
      set(lastSegExecutionTraceAtom, data.seg_execution_trace);
      set(activeSegIdAtom, data.seg_execution_trace.seg_id);

      const finalStates = new Map<string, 'idle' | 'running' | 'success' | 'error'>();
      for (const n of data.seg_execution_trace.nodes) {
        finalStates.set(n.node_id, n.result_status === 'error' ? 'error' : 'success');
      }
      set(segNodeStatesAtom, finalStates);

      // The trace's own order, which includes prior-turn nodes
      const seenIds = new Set<string>();
      const traceOrder: string[] = [];
      for (const n of data.seg_execution_trace.nodes) {
        if (!seenIds.has(n.node_id)) {
          seenIds.add(n.node_id);
          traceOrder.push(n.node_id);
        }
      }
      set(segNodeOrderAtom, traceOrder);

      // Backfill any nodes that weren't captured by real-time events (e.g. prior-turn nodes)
      set(segNodeDetailsAtom, (prev) => {
        const next = new Map(prev);
        for (const n of data.seg_execution_trace!.nodes) {
          const existing = next.get(n.node_id);
          // Only backfill; don't overwrite richer real-time data
          if (!existing?.output && !existing?.duration_ms) {
            next.set(n.node_id, {
              node_type: n.node_type,
              node_label: n.node_label,
              result_status: n.result_status,
              duration_ms: n.duration_ms,
              inputs: n.inputs,
              output: n.output,
              error_detail: n.error_detail,
              thinking_text: n.thinking_text,
            });
          }
        }
        return next;
      });
    }

    messageHandler?.(data);
  };

  socketInstance.on('message', (data) => {
    // Handle nested payload structure (e.g., { type: "MESSAGE_TYPE_DRAFT_MESSAGE", payload: {...} })
    const raw = (
      'payload' in data
        ? (data as unknown as { type: string; payload: Record<string, unknown>; timestamp: number }).payload
        : data
    ) as Record<string, unknown>;
    // CRM_AI_ASSIST broadcasts carry camelCase keys (requestId / rulesUsed);
    // normalize to the snake_case TestRuleResultPayload so request_id reaches
    // consumers (drives the dedup + the AI-reply styling) and rules_used populates.
    const actualData = {
      ...raw,
      request_id: raw.request_id ?? raw.requestId,
      rules_used: raw.rules_used ?? raw.rulesUsed,
    } as unknown as TestRuleResultPayload;
    handleMessageData(actualData);
  });

  socketInstance.on('test_rule_result', (data) => {
    handleMessageData(data);
  });

  socketInstance.on('tool_execution_event', (data) => {
    // Account-broadcast: only reflect tool activity for this session's runs.
    if (data.request_id && !runRequestIds.has(data.request_id)) {
      return;
    }
    if (data.execution_event === 'start') {
      set(toolExecutionTextAtom, data.display_text || DEFAULT_TOOL_EXECUTION_TEXT);
      set(isToolExecutingAtom, true);

      // Clear any existing timeout
      clearToolTimeout();

      // Set timeout to auto-clear
      toolTimeoutId = setTimeout(() => {
        set(toolExecutionTextAtom, '');
        set(isToolExecutingAtom, false);
      }, TOOL_EXECUTION_TIMEOUT);
    } else if (data.execution_event === 'end') {
      clearToolTimeout();
      set(toolExecutionTextAtom, '');
      set(isToolExecutingAtom, false);
    }

    toolExecutionHandler?.(data);
  });

  socketInstance.on('thinking_event', (data) => {
    if (data.sentinel_ticket_id != null) {
      const active = get(sentinelWorkspaceSocketIdentityAtom);
      if (!isActiveSentinelThinkingEvent(data, active)) return;
      if (isInternalTraceStep({ toolName: data.tool_name, nodeId: data.node_id })) {
        return;
      }
      const sentinelRequestId = data.sentinel_request_id!;
      if (active && active.requestId == null) {
        set(sentinelWorkspaceSocketIdentityAtom, {
          ...active,
          requestId: sentinelRequestId,
          ignoredRequestIds: undefined,
        });
      }
      set(sentinelThinkingStepsAtom, (previous) => {
        return mergeSentinelThinkingEvent(previous, data, sentinelRequestId);
      });
      return;
    }
    if (isInternalTraceStep({ toolName: data.tool_name, nodeId: data.node_id })) {
      return;
    }
    // Request ids are generated client-side at send time, so an event carrying a
    // request id outside the current run epoch belongs to another session's run
    // (account-room delivery) or a stale one after Reset. Ignore it wholesale:
    // never reset the live timeline, seg maps, or thinking text for it.
    if (data.request_id && !runRequestIds.has(data.request_id)) {
      return;
    }
    // Append an ordered, timestamped step for the simulator timeline. One entry
    // per node_start (V2 also emits node_end, skipped here to avoid duplicates).
    if (data.event === 'node_start') {
      set(thinkingStepsAtom, (prev) => {
        const step: ThinkingStep = {
          nodeId: data.node_id,
          nodeType: data.node_type,
          label: data.node_label || data.thinking_text || data.node_id,
          thinkingText: data.thinking_text,
          timestamp: data.timestamp || Date.now(),
          requestId: data.request_id,
          toolName: data.tool_name,
          toolArgs: data.tool_args,
        };
        // Each thinking_event is delivered twice, once to the conversation room and
        // once to the account room this socket is in. De-dupe by node + timestamp
        // so the timeline shows one entry per real step.
        if (prev.some((s) => s.nodeId === step.nodeId && s.timestamp === step.timestamp)) return prev;
        return [...prev, step];
      });
    } else if (data.event === 'node_end') {
      // Resolve the matching pending step (same node_id); !s.done skips the duplicate.
      // An awaiting-approval step stays patchable so a later node_end can settle it.
      set(thinkingStepsAtom, (prev) => {
        let patched = false;
        const next = prev.map((s) => {
          if (!patched && s.nodeId === data.node_id && (!s.done || s.outcome === 'awaiting_approval')) {
            patched = true;
            return {
              ...s,
              label: data.node_label || data.thinking_text || s.label,
              resultSummary: data.result_summary ?? s.resultSummary,
              resultItems: data.result_items ?? s.resultItems,
              durationMs: data.duration_ms ?? s.durationMs,
              outcome: data.outcome ?? s.outcome,
              isWrite: data.is_write ?? s.isWrite,
              simulated: data.simulated ?? s.simulated,
              simulatedWrite: data.simulated_write ?? s.simulatedWrite,
              grounded: data.grounded ?? s.grounded,
              toolOutput: data.tool_output ?? s.toolOutput,
              done: true,
            };
          }
          return s;
        });
        return patched ? next : prev;
      });
    }

    // Pre-execution events from Brahma's orchestrator only update the thinking text
    if (['orchestrator', 'v3_orchestrator', 'policy_tool', 'tool_call', 'v3_stage', 'policy_state', 'react_state', 'rejection'].includes(data.node_type)) {
      if (data.thinking_text) set(activeThinkingTextAtom, data.thinking_text);
      return;
    }

    // Reset per-run state when a new request begins (request_id change).
    // Using request_id instead of step===0 handles multi-turn resumptions where
    // the first node_start of a new turn arrives at step > 0.
    if (data.event === 'node_start' && data.request_id !== lastThinkingRequestId) {
      lastThinkingRequestId = data.request_id;
      set(segNodeStatesAtom, new Map());
      set(segNodeDetailsAtom, new Map());
      set(segNodeOrderAtom, []);
    }

    set(activeSegIdAtom, data.seg_id);

    if (data.event === 'node_start') {
      if (data.thinking_text) set(activeThinkingTextAtom, data.thinking_text);
      set(segNodeStatesAtom, (prev) => {
        const next = new Map(prev);
        next.set(data.node_id, 'running');
        return next;
      });
      // Capture inputs and thinking_text early so node_end can inherit them
      set(segNodeDetailsAtom, (prev) => {
        const next = new Map(prev);
        const existing = prev.get(data.node_id) ?? {};
        next.set(data.node_id, {
          ...existing,
          inputs: data.inputs,
          thinking_text: data.thinking_text,
          node_type: data.node_type,
          node_label: data.node_label,
          step: data.step,
        });
        return next;
      });
    } else if (data.event === 'node_end') {
      const finalState = data.result_status === 'error' ? 'error' : 'success';
      set(segNodeStatesAtom, (prev) => {
        const next = new Map(prev);
        next.set(data.node_id, finalState);
        return next;
      });
      set(segNodeDetailsAtom, (prev) => {
        const next = new Map(prev);
        const existing = prev.get(data.node_id) ?? {};
        next.set(data.node_id, {
          // Preserve inputs and thinking_text captured during node_start
          inputs: existing.inputs,
          thinking_text: existing.thinking_text,
          output: data.output,
          error_detail: data.error_detail,
          duration_ms: data.duration_ms,
          result_status: data.result_status,
          node_type: data.node_type,
          node_label: data.node_label,
          step: data.step,
        });
        return next;
      });
      set(segNodeOrderAtom, (prev) => {
        if (prev.includes(data.node_id)) return prev;
        return [...prev, data.node_id];
      });
    }

    thinkingEventHandler?.(data);
  });

  socketInstance.on('source_status_updated', (data) => {
    sourceStatusUpdateHandler?.(data);
  });

  socketInstance.on('source_status_update_error', (data) => {
    sourceStatusErrorHandler?.(data);
  });

  socketInstance.on('workflow_compiled', (data) => {
    workflowCompiledHandler?.(data);
  });

  socketInstance.on('workflow_compile_failed', (data) => {
    workflowCompileFailedHandler?.(data);
  });

  socketInstance.on('persona_generated', (data) => {
    personaGeneratedHandler?.(data);
  });

  socketInstance.on('persona_generation_failed', (data) => {
    personaGenerationFailedHandler?.(data);
  });

  socketInstance.on('decision_question', (data) => {
    decisionQuestionHandler?.(data);
  });

  socketInstance.on('decision_progress', (data) => {
    decisionProgressHandler?.(data);
  });

  socketInstance.on('decision_failed', (data) => {
    decisionFailedHandler?.(data);
  });

  socketInstance.on('decision_complete', (data) => {
    decisionCompleteHandler?.(data);
  });

  // Scoring runs off the generation's critical path, so this lands after the run is already
  // done — without it the panel shows "not scored" for a workflow that was.
  socketInstance.on('decision_scored', (data) => {
    decisionScoredHandler?.(data);
  });

  socketInstance.on('back_testing_bucketing', (data) => {
    backTestingBucketingHandler?.(data);
  });

  socketInstance.on('back_testing_protocol_compiling', (data) => {
    setProtocolCompiling(data.sopId);
  });

  socketInstance.on('back_testing_types_reconciled', (data) => {
    void refreshQueries(backTestingTypesKey(data.sopId));
  });

  socketInstance.on('back_testing_types_reconciling', (data) => {
    setReconcileProgress(data.sopId, data.progress);
  });

  socketInstance.on('back_testing_types_reconcile_failed', (data) => {
    void refreshQueries(backTestingTypesKey(data.sopId));
  });

  socketInstance.on('back_testing_bulk_run', (data) => {
    backTestingBulkRunHandler?.(data);
    void refreshQueries([BULK_RUNS_QUERY_ROOT, data.sopId, data.scenarioTypeId]);
  });

  socketInstance.on('gap_test_run', (data) => {
    gapTestRunHandler?.(data);
  });

  socketInstance.on('back_testing_investigation', (data) => {
    backTestingInvestigationHandler?.(data);
  });

  socketInstance.on('back_testing_investigation_step', (data) => {
    backTestingInvestigationStepHandler?.(data);
  });

  // Straight into the review store, not a screen's handler: an investigation runs for
  // minutes, and its verdict must land even when the simulator is not on screen.
  socketInstance.on('playground_investigation', (data) => {
    set(concludeInvestigationAtom, data.requestId, { investigation: data.investigation, reason: data.reason });
  });

  socketInstance.on('playground_investigation_step', (data) => {
    set(appendInvestigationStepAtom, data.requestId, { text: data.step, ts: data.ts });
  });

  // Only the tab that asked takes the run as its own: another tab can hold the same inbox conversation.
  socketInstance.on('simulator_run_saved', (data) => {
    if (runRequestIds.has(data.requestId) && data.conversationId === get(conversationIdAtom)) {
      set(runOnScreenAtom, data.requestId);
    }
    void invalidateQueries([SIMULATOR_RUNS_QUERY_ROOT]);
  });

  socketInstance.on('playground_chat_turn', (data) => {
    playgroundChatTurnHandler?.(data);
  });

  socketInstance.on('simulation_dataset_case', (data) => {
    simulationDatasetCaseHandler?.(data);
  });

  socketInstance.on('simulation_dataset_case_step', (data) => {
    simulationDatasetCaseStepHandler?.(data);
  });

  socketInstance.on('simulation_dataset_run', (data) => {
    simulationDatasetRunHandler?.(data);
    void refreshQueries([DATASET_RUNS_QUERY_ROOT, data.datasetId]);
  });

  // Untouched tickets keep their arrays, so only the rows that changed redraw.
  socketInstance.on('ticket_presence', ({ ticketId, viewers }) => {
    set(ticketViewersAtom, (previous) => {
      const next = { ...previous };
      if (viewers.length > 0) next[ticketId] = viewers;
      else delete next[ticketId];
      return next;
    });
  });

  socketInstance.on('ticket_presence_snapshot', ({ tickets }) => {
    set(ticketViewersAtom, Object.fromEntries(tickets.map((ticket) => [ticket.ticketId, ticket.viewers])));
  });

  socketInstance.on('sentinel_presence_changed', ({ userId, buId, online }) => {
    const key = `${buId}:${userId}`;
    presenceChangeCount += 1;
    const order = presenceChangeCount;
    set(sentinelPresenceChangesAtom, (previous) => ({ ...previous, [key]: online }));
    set(sentinelPresenceOrderAtom, (previous) => ({ ...previous, [key]: order }));
  });

  socketInstance.on('sentinel_ticket_assigned', (assignment) => {
    refreshSentinelTicket({ ticketId: assignment.ticketId });
    set(latestSentinelAssignmentAtom, assignment);
  });
  socketInstance.on('sentinel_ticket_unassigned', ({ ticketId }) => refreshSentinelTicket({ ticketId }));
  // Open / Closed comes from the helpdesk, and can change with no owner or slot change.
  socketInstance.on('sentinel_ticket_status_changed', ({ conversationId }) => {
    refreshSentinelTicket({ conversationId });
    // Automation review shows the same Open / Closed and its Close on each reply.
    void invalidateQueries(['automation-replies']);
  });
  // A change with no owner or Open / Closed change, such as automation sending its reply.
  socketInstance.on('sentinel_ticket_updated', ({ ticketId }) => refreshSentinelTicket({ ticketId }));

  return socketInstance;
});

/**
 * Authenticate the socket connection
 */
export const authenticateAtom = atom(null, (get, set, payload: Omit<AuthenticatePayload, 'clientId'>) => {
  if (!socketInstance?.connected) {
    return;
  }

  const clientId = get(clientIdAtom);
  if (!clientId) {
    return;
  }

  const authPayload = {
    ...payload,
    clientId
  };

  socketInstance.emit('authenticate', authPayload);

  set(websocketStatusAtom, 'connected');
});

/**
 * Send a message through the socket
 */
export const sendMessageAtom = atom(null, (_get, set, payload: Omit<MessagePayload, 'requestId'>) => {
  if (!socketInstance?.connected) {
    return null;
  }

  const requestId = generateRequestId();
  runRequestIds = new Set([requestId]);
  set(isWaitingAtom, true);
  set(thinkingStepsAtom, []);
  set(lastRunErrorAtom, null);
  set(lastRunApprovalAtom, null);
  // The previous run's details stay visible during this one and update when the
  // new response lands; only Reset clears them.

  const messagePayload: MessagePayload = {
    ...payload,
    requestId
  };

  socketInstance.emit('message', messagePayload);

  return requestId;
});

/**
 * Disconnect the socket
 */
export const disconnectWebSocketAtom = atom(null, (_get, set) => {
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }
  set(websocketStatusAtom, 'disconnected');
  set(socketIdAtom, null);
  set(isWaitingAtom, false);
  clearToolTimeout();
  set(toolExecutionTextAtom, '');
  set(isToolExecutingAtom, false);
  set(sentinelThinkingStepsAtom, []);
});

/**
 * Set message handler callback
 */
export const setMessageHandlerAtom = atom(null, (_get, _set, handler: MessageHandler) => {
  messageHandler = handler;
});

/**
 * Set error handler callback
 */
export const setErrorHandlerAtom = atom(null, (_get, _set, handler: ErrorHandler) => {
  errorHandler = handler;
});

/**
 * Set tool execution handler callback
 */
export const setToolExecutionHandlerAtom = atom(null, (_get, _set, handler: ToolExecutionHandler) => {
  toolExecutionHandler = handler;
});

/**
 * Set source status update handler callback
 */
export const setSourceStatusUpdateHandlerAtom = atom(null, (_get, _set, handler: SourceStatusUpdateHandler) => {
  sourceStatusUpdateHandler = handler;
});

/**
 * Set source status error handler callback
 */
export const setSourceStatusErrorHandlerAtom = atom(null, (_get, _set, handler: SourceStatusErrorHandler) => {
  sourceStatusErrorHandler = handler;
});

/**
 * Set workflow compiled handler callback
 */
export const setWorkflowCompiledHandlerAtom = atom(null, (_get, _set, handler: WorkflowCompiledHandler) => {
  workflowCompiledHandler = handler;
});

/**
 * Set workflow compile failed handler callback
 */
export const setWorkflowCompileFailedHandlerAtom = atom(null, (_get, _set, handler: WorkflowCompileFailedHandler) => {
  workflowCompileFailedHandler = handler;
});

/**
 * Set SEG node event handler callback
 */
export const setThinkingEventHandlerAtom = atom(null, (_get, _set, handler: ThinkingEventHandler) => {
  thinkingEventHandler = handler;
});

/** SOP → Workflow decision-collection (HITL) handler setters. */
export const setDecisionQuestionHandlerAtom = atom(null, (_get, _set, handler: DecisionQuestionHandler) => {
  decisionQuestionHandler = handler;
});
export const setDecisionProgressHandlerAtom = atom(null, (_get, _set, handler: DecisionProgressHandler) => {
  decisionProgressHandler = handler;
});
export const setDecisionFailedHandlerAtom = atom(null, (_get, _set, handler: DecisionFailedHandler) => {
  decisionFailedHandler = handler;
});
export const setDecisionCompleteHandlerAtom = atom(null, (_get, _set, handler: DecisionCompleteHandler) => {
  decisionCompleteHandler = handler;
});
export const setDecisionScoredHandlerAtom = atom(null, (_get, _set, handler: DecisionScoredHandler) => {
  decisionScoredHandler = handler;
});

/** Back-testing bucketing progress; null unregisters (the panel that listened is gone). */
export const setBackTestingBucketingHandlerAtom = atom(
  null,
  (_get, _set, handler: BackTestingBucketingHandler | null) => {
    backTestingBucketingHandler = handler;
  }
);

/** Back-testing bulk-run progress; null unregisters (the screen that listened is gone). */
export const setBackTestingBulkRunHandlerAtom = atom(
  null,
  (_get, _set, handler: BackTestingBulkRunHandler | null) => {
    backTestingBulkRunHandler = handler;
  }
);

/** A gap's testing-rail run-set progress; null unregisters (the rail unmounted). */
export const setGapTestRunHandlerAtom = atom(null, (_get, _set, handler: GapTestRunHandler | null) => {
  gapTestRunHandler = handler;
});

/** A flagged run's investigation verdict landed; null unregisters (the screen that listened is gone). */
export const setBackTestingInvestigationHandlerAtom = atom(
  null,
  (_get, _set, handler: BackTestingInvestigationHandler | null) => {
    backTestingInvestigationHandler = handler;
  }
);

/** A live step of an in-flight investigation; null unregisters (the screen that listened is gone). */
export const setBackTestingInvestigationStepHandlerAtom = atom(
  null,
  (_get, _set, handler: BackTestingInvestigationStepHandler | null) => {
    backTestingInvestigationStepHandler = handler;
  }
);

/** The Playground page showing a live turn listens here; null when it is not on screen. */
export const setPlaygroundChatTurnHandlerAtom = atom(
  null,
  (_get, _set, handler: PlaygroundChatTurnHandler | null) => {
    playgroundChatTurnHandler = handler;
  }
);

/** A dataset case started, was prefilled or failed; null unregisters (the screen that listened is gone). */
export const setSimulationDatasetCaseHandlerAtom = atom(
  null,
  (_get, _set, handler: SimulationDatasetCaseHandler | null) => {
    simulationDatasetCaseHandler = handler;
  }
);

/** A live step of a dataset case in capture; null unregisters. */
export const setSimulationDatasetCaseStepHandlerAtom = atom(
  null,
  (_get, _set, handler: SimulationDatasetCaseStepHandler | null) => {
    simulationDatasetCaseStepHandler = handler;
  }
);

/** A dataset's run moved; null unregisters. */
export const setSimulationDatasetRunHandlerAtom = atom(null, (_get, _set, handler: SimulationDatasetRunHandler | null) => {
  simulationDatasetRunHandler = handler;
});

/** Emit a human answer to the current decision question. */
export const sendDecisionAnswerAtom = atom(
  null,
  (_get, _set, payload: { sessionId: string; decisionId: string; answer: unknown }) => {
    if (!socketInstance?.connected) {
      return false;
    }
    socketInstance.emit('decision_answer', payload);
    return true;
  },
);

/**
 * Set persona generated handler callback
 */
export const setPersonaGeneratedHandlerAtom = atom(null, (_get, _set, handler: PersonaGeneratedHandler) => {
  personaGeneratedHandler = handler;
});

/**
 * Set persona generation failed handler callback
 */
export const setPersonaGenerationFailedHandlerAtom = atom(
  null,
  (_get, _set, handler: PersonaGenerationFailedHandler) => {
    personaGenerationFailedHandler = handler;
  }
);

/**
 * Clear SEG node execution state (call before starting a new test)
 */
export const clearSegNodeStatesAtom = atom(null, (_get, set) => {
  set(segNodeStatesAtom, new Map());
  set(segNodeDetailsAtom, new Map());
  set(segNodeOrderAtom, []);
  set(activeSegIdAtom, null);
  set(lastSegExecutionTraceAtom, null);
  set(activeThinkingTextAtom, null);
  set(thinkingStepsAtom, []);
  set(lastRunDetailsAtom, null);
  set(lastRunErrorAtom, null);
  set(lastRunApprovalAtom, null);
  runRequestIds = new Set();
  // Recover the composer if a run hung: the backend never sent a final message
  // or error, so isWaiting was never flipped back.
  set(isWaitingAtom, false);
});

/**
 * THE simulator reset: the kept thread with its conversation id and the past
 * conversation it was loaded from, the whole simulation profile (the simulated
 * customer with it), every piece of run state, the run epoch and the dedupe
 * cursors.
 */
export const resetSimulatorAtom = atom(null, (_get, set) => {
  set(chatMessagesAtom, []);
  set(conversationIdAtom, crypto.randomUUID());
  set(loadedThreadAtom, null);
  set(reopenedLinksAtom, null);
  set(describedScenarioAtom, EMPTY_DESCRIBED_SCENARIO);
  set(cancelDescribeFlowAtom);
  set(runOnScreenAtom, null);
  // Reviews go with the thread, not with a run: clearSegNodeStates runs on every
  // Ask AI, and each reply keeps the verdict it earned.
  set(clearRunReviewsAtom);
  // The customer is part of the profile, so this clears them with it.
  set(simulationProfileAtom, EMPTY_SIMULATION_PROFILE);
  set(openSectionsAtom, []);
  set(clearSegNodeStatesAtom);
  set(lastRulesUsedAtom, null);
  clearToolTimeout();
  set(toolExecutionTextAtom, '');
  set(isToolExecutingAtom, false);
  lastProcessedRequestId = null;
  lastThinkingRequestId = null;
});

// ============================================================================
// Derived Atoms
// ============================================================================

/**
 * Check if connected
 */
export const isConnectedAtom = atom((get) => {
  const status = get(websocketStatusAtom);
  return status === 'connected' || status === 'authenticated';
});

/**
 * Signals that the socket needs to re-authenticate (e.g. after token expiry)
 */
export const needsReauthAtom = atom<boolean>(false);
